import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ImpressumModal, DatenschutzModal } from './LegalModals';
type ThankYouPageProps = {
  onClose: () => void;
};
function ThankYouHeader() {
  return <motion.header className="fixed top-0 left-0 right-0 z-50 bg-slate-900/95 backdrop-blur-md shadow-lg" initial={{
    y: -100
  }} animate={{
    y: 0
  }} transition={{
    duration: 0.5
  }} data-id="element-394">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-395">
        <div className="flex items-center justify-between h-16 sm:h-20" data-id="element-396">
          <a href="#" className="text-white font-bold text-xl sm:text-2xl tracking-tight" data-id="element-397">
            CR Careers
          </a>
        </div>
      </div>
    </motion.header>;
}
function ThankYouFooter() {
  const [showImpressum, setShowImpressum] = useState(false);
  const [showDatenschutz, setShowDatenschutz] = useState(false);
  return <>
      <footer className="bg-slate-950 border-t border-slate-800" data-id="element-398">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-399">
          <div className="text-center space-y-6" data-id="element-400">
            <div className="text-white font-bold text-2xl mb-4" data-id="element-401">CR Careers</div>

            <div className="flex justify-center gap-6 text-sm text-slate-400" data-id="element-402">
              <button onClick={() => setShowImpressum(true)} className="hover:text-white transition-colors" data-id="element-403">
                Impressum
              </button>
              <span className="text-slate-700" data-id="element-404">|</span>
              <button onClick={() => setShowDatenschutz(true)} className="hover:text-white transition-colors" data-id="element-405">
                Datenschutz
              </button>
            </div>

            <p className="text-slate-400 text-sm" data-id="element-406">
              © {new Date().getFullYear()} CR Careers. All rights reserved.
            </p>
            <p className="text-slate-500 text-xs max-w-3xl mx-auto leading-relaxed" data-id="element-407">
              NOT GOOGLE: This site is not a part of the Google website or
              Google Inc. Additionally, This site is NOT endorsed by Google in
              any way. This site is not a part of the Facebook website or
              Facebook Inc. Additionally, This site is NOT endorsed by Facebook
              in any way. FACEBOOK is a trademark of FACEBOOK, Inc.
            </p>
          </div>
        </div>
      </footer>
      <AnimatePresence data-id="element-408">
        {showImpressum && <ImpressumModal isOpen={showImpressum} onClose={() => setShowImpressum(false)} data-id="element-409" />}
        {showDatenschutz && <DatenschutzModal isOpen={showDatenschutz} onClose={() => setShowDatenschutz(false)} data-id="element-410" />}
      </AnimatePresence>
    </>;
}
export function ThankYouPage({
  onClose
}: ThankYouPageProps) {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  // Track Lead event when thank you page is shown
  useEffect(() => {
    if (window.fbq) {
      window.fbq('track', 'Lead');
    }
  }, []);
  return <div className="min-h-screen w-full bg-slate-900 overflow-x-hidden" data-id="element-411">
      <ThankYouHeader data-id="element-412" />

      <main className="overflow-x-hidden" data-id="element-413">
        <section className="min-h-screen flex items-center justify-center bg-gradient-to-b from-slate-900 via-slate-900 to-slate-800" data-id="element-414">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-32" data-id="element-415">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} className="text-center space-y-12" data-id="element-416">
              {/* Heading */}
              <div className="space-y-4" data-id="element-417">
                <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white" data-id="element-418">
                  Important! Watch the Video
                </h1>
                <p className="text-xl sm:text-2xl text-slate-400" data-id="element-419">
                  The application process is not done
                </p>
              </div>

              {/* YouTube Video Embed */}
              <motion.div initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              delay: 0.2,
              duration: 0.5
            }} className="relative w-full max-w-4xl mx-auto aspect-video rounded-2xl overflow-hidden shadow-2xl border border-slate-800" data-id="element-420">
                <iframe width="100%" height="100%" src="https://www.youtube.com/embed/MGexbdfD9kM" title="Next Steps for the Technical Assistant" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="absolute inset-0" data-id="element-421"></iframe>
              </motion.div>

              {/* Instructions */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.4,
              duration: 0.5
            }} className="space-y-8 max-w-3xl mx-auto pt-8" data-id="element-422">
                <p className="text-lg sm:text-xl text-slate-300 leading-relaxed" data-id="element-423">
                  Check your email inbox right now and send us the{' '}
                  <span className="font-bold text-white" data-id="element-424">
                    result of the survey
                  </span>{' '}
                  and your <span className="font-bold text-white" data-id="element-425">CV</span>.
                </p>

                <p className="text-lg sm:text-xl text-slate-300 leading-relaxed" data-id="element-426">
                  Then schedule an appointment (the link is in the email).
                </p>

                <p className="text-lg sm:text-xl text-slate-300 leading-relaxed" data-id="element-427">
                  Make sure to get familiar with{' '}
                  <span className="font-bold text-white" data-id="element-428">Google Meet</span>.
                </p>

                <p className="text-lg sm:text-xl text-slate-300 leading-relaxed" data-id="element-429">
                  And that your{' '}
                  <span className="font-bold text-white" data-id="element-430">camera</span> and{' '}
                  <span className="font-bold text-white" data-id="element-431">microphone</span> is
                  working during the meeting!
                </p>
              </motion.div>
            </motion.div>
          </div>
        </section>
      </main>

      <ThankYouFooter data-id="element-432" />
    </div>;
}